import { Alert, H2, SubBody } from '@rikstv/shared-components';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Layout, Loader } from '~/components/shared';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { useComparableSubscriptions } from '../choose-subscription/useComparableSubscriptions.ts';
import { ispServiceAvailabilityCheck } from './internet.api.ts';
import { useInternetFlowStore } from './useInternetStore';

import useSWRImmutable from 'swr/immutable';
import { DisabledsInputField } from './components/DisabledInputField.tsx';
import { InternetBundleCard } from './components/InternetBundleCard.tsx';
import { TvPackageOnlyCard } from './components/TvPackageOnlyCard.tsx';
import { useLazySanityData } from './hooks/useLazySanityData.ts';
import { mapSubsriptionsToInternetBundle } from './internet.utils.ts';
import { Route_InternetLandingPage } from './InternetLandingPage.tsx';
import classes from './InternetOfferSubPage.module.css';

export const Route_InternetOfferSubPage = 'tilbud';

export const InternetOfferSubPage: FC = () => {
  const { availableProducts, isPending, legalTerms, internetBundles, selectedAddress } = useOfferPageLogic();

  // return null while waiting for redirect that happens in useOfferPageLogic
  if (selectedAddress?.addrType !== 'single') {
    return null;
  }
  if (isPending) {
    return (
      <Layout>
        <Card>
          <Loader text="" />
          <H2 className={classes.centerText}>Nå sjekker vi hva du kan få...</H2>
          <p className={classes.centerText}>Adresse: {selectedAddress.description}</p>
          <br />
        </Card>
      </Layout>
    );
  }
  if (!availableProducts || availableProducts.length === 0) {
    return (
      <>
        <Layout wide>
          <Card>
            <div>
              <Alert intent="info" variant="inline">
                Beklager! NextGenTel tilbyr ikke internett på denne adressen enda. Du kan fremdeles bestille TV-pakke.
              </Alert>
            </div>
            <br />
            <DisabledsInputField selectedAddress={selectedAddress} />
          </Card>
          <TvPackageOnlyCard noInternetPackages />
        </Layout>
        <SupportFooter />
      </>
    );
  }

  // Display TV+Internet packages
  return (
    <>
      <Layout wide>
        <Card>
          <div>
            <Alert intent="success" variant="inline">
              NextGenTel leverer internett på denne adressen. Velg pakken som passer deg nedenfor.
            </Alert>
          </div>
          <br />
          <DisabledsInputField selectedAddress={selectedAddress} />
        </Card>
        <H2>Velg TV- og internettpakke</H2>
        <br />
        <section>
          <div className={classes.bundleList}>
            {internetBundles.map((bundle) => (
              <InternetBundleCard key={bundle.subscription.campaignUrl} bundle={bundle} />
            ))}
          </div>
          <br />
          <SubBody style={{ color: 'var(--disabled-text)', whiteSpace: 'pre-wrap' }}>{legalTerms}</SubBody>
          <br />
          <TvPackageOnlyCard />
        </section>
      </Layout>
      <SupportFooter />
    </>
  );
};

const useOfferPageLogic = () => {
  const navigate = useNavigate();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress);
  const availableProducts = useInternetFlowStore((s) => s.availableServiceOrderProducts);
  const setAvailabilityResponse = useInternetFlowStore((s) => s.setAvailabilityResponse);
  const comparableSubscriptions = useComparableSubscriptions('kampanje', 'bolig');
  // sort subscriptions by price and filter out the ones we wont need
  const {
    broadbandProducts,
    displayData: { legalTerms },
  } = useLazySanityData();
  const internetBundles = useMemo(() => {
    if (comparableSubscriptions.data && availableProducts) {
      return mapSubsriptionsToInternetBundle(comparableSubscriptions.data, availableProducts, broadbandProducts);
    }
    return [];
  }, [comparableSubscriptions.data, availableProducts, broadbandProducts]);

  const {
    isLoading: isPending,
    data,
    error,
  } = useSWRImmutable(selectedAddress ? [selectedAddress] : null, ([addr]) => ispServiceAvailabilityCheck(addr));
  useEffect(() => {
    if (data) {
      setAvailabilityResponse(data);
    } else if (error) {
      console.error('Error fetching ISP availability:', error);
      setAvailabilityResponse(undefined);
    }
  }, [data, error, setAvailabilityResponse]);

  // Redirect if no address selected
  useEffect(() => {
    if (!selectedAddress) {
      // Redirect if address is not of correct type or null
      // Also when address is reset via button
      navigate(`/${Route_InternetLandingPage}`);
    }
  }, [selectedAddress, navigate]);

  return {
    availableProducts,
    isPending,
    internetBundles,
    legalTerms,
    selectedAddress,
  };
};
